import { useState } from 'react';
import { Box, Button, Paper, Typography, Alert } from '@mui/material';
import SearchBar from '../../../components/SearchBar';
import AssignNewMachineTable from './MachineTable';
import useStyles from './style';
import { useUpdateModemByIdMutation, useLazyGetModemResyncStatusQuery } from '../../../services/Modems/ModemsService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import { useSelector } from 'react-redux';
import ConfirmationModal from '../../../components/ConfirmationModel';
import Datepicker from '../../../components/shared/Datepicker';

const AssignNewAsset = ({ setAssignNewMachine, modemId }) => {
  const classes = useStyles();
  const [updateModemApi] = useUpdateModemByIdMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const [getModemResyncStatus] = useLazyGetModemResyncStatusQuery();
  const [searchText, setSearchText] = useState('');
  const [machineData, setMachineData] = useState({});
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const isAssetSelected = !!machineData?.id;
  const [resyncWarning, setResyncWarning] = useState('');
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  // Datepicker state
  const [inEffectFrom, setInEffectFrom] = useState(null);
  const [inEffectFromError, setInEffectFromError] = useState('');

  // Override modal state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [apiError, setApiError] = useState('');

  const formatDateOnly = (date) => {
    if (!date) return null;
    return new Date(date).toISOString().slice(0, 10);
  };
  const onSetSelectedRows = (row) => {
    setMachineData(row[0]);
  };

  const searchHandler = (params) => {
    setSearchText(params);
  };

  const onReset = () => {
    setSearchText('');
  };

  const onSaveHandler = async () => {
    setApiError('');
    setResyncWarning('');
    // Validate inEffectFrom
    if (!inEffectFrom) {
      setInEffectFromError('In Effect From date is required');
      return;
    }
    setInEffectFromError('');

    if (isResyncEnabled) {
      // Check resync status of the modem
      if (modemId?.modemId) {
        try {
          const modemResyncResult = await getModemResyncStatus(modemId.modemId).unwrap();
          if (modemResyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this modem. Please try again after resync is complete.',
            );
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }

      // Check resync status of selected asset
      if (machineData?.id) {
        try {
          const resyncResult = await getAssetResyncStatus(machineData.id).unwrap();
          if (resyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            return;
          }
        } catch {
          // If API fails, proceed with assignment
        }
      }
    }
    const payload = {
      id: modemId.modemId,
      version: modemId.version,
      type: modemId?.type || '',
      installedDate: modemId?.installedDate || '',
      price: modemId?.price || 0,
      loginId: modemId?.loginId || '',
      password: modemId?.password || '',
      serialNumber: modemId?.serialNumber || '',
      MACAddress: modemId?.MACAddress || null,
      assetId: machineData?.id,
      updatedBy,
      inEffectFrom: formatDateOnly(inEffectFrom),
      inEffectTo: machineData?.inEffectTo || null,
      override: false,
    };
    try {
      const response = await updateModemApi(payload).unwrap();
      if (response) {
        setAssignNewMachine(false);
      }
    } catch (error) {
      if (error.status === 409) {
        setApiError(error.data?.message || 'A conflict occurred while saving the record.');
        setShowOverrideModal(true);
        setPendingFormData(payload);
      }
    }
  };

  const handleOverrideConfirm = async () => {
    setShowOverrideModal(false);
    if (!pendingFormData) return;
    try {
      const response = await updateModemApi({ ...pendingFormData, override: true }).unwrap();
      if (response) {
        setAssignNewMachine(false);
      }
      setPendingFormData(null);
      setApiError('');
    } catch {
      setApiError('Failed to override and save.');
    }
  };

  const handleOverrideCancel = () => {
    setShowOverrideModal(false);
    setPendingFormData(null);
    setApiError('');
  };

  const onCancelHandler = () => {
    setAssignNewMachine(false);
  };

  return (
    <Box component={Paper} className={classes.assignMachine}>
      <Box className={classes.assignNewMachineTitle}>
        <Typography variant="h2">Assign New Machine</Typography>
      </Box>
      <Box className={classes.machineSearch}>
        <SearchBar onSearch={searchHandler} onReset={onReset} />
      </Box>
      <AssignNewMachineTable onSelect={onSetSelectedRows} searchParams={searchText} />
      {resyncWarning && (
        <Box sx={{ m: 2 }}>
          <Alert severity="warning">{resyncWarning}</Alert>
        </Box>
      )}
      <Box sx={{ maxWidth: 300, mt: 2, m: 2 }}>
        <Datepicker
          label="In Effect From"
          value={inEffectFrom}
          onChange={setInEffectFrom}
          inputFormat="MM/DD/YYYY"
          maxDate={new Date()}
          error={!!inEffectFromError}
          helperText={inEffectFromError}
          required
          disabled={!isAssetSelected}
        />
      </Box>
      <Box className={classes.btnWrap}>
        <Button variant="contained" onClick={onSaveHandler}>
          SAVE
        </Button>
        <Button onClick={onCancelHandler}>CANCEL</Button>
      </Box>
      <ConfirmationModal
        isOpen={showOverrideModal}
        title="Update Confirmation"
        msg={apiError || 'There is a conflict with the modem data. Do you want to override and continue?'}
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'confirm') {
            handleOverrideConfirm();
          } else {
            handleOverrideCancel();
          }
        }}
      />
    </Box>
  );
};

export default AssignNewAsset;
